"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DefaultPreviewEmailPayloadSchema = exports.AssignmentConfirmationJobPayloadSchema = exports.ReceivableResponseTapePreviewsFromStrategyPayloadSchema = exports.SingleReceivableResponseTapePreviewPayloadSchema = exports.Assignment = exports.ReceivableResponseTapePreviewJobs = exports.Preview = exports.CreditFacility = exports.ReceivableBaseFieldsSchema = exports.AssignmentExclusionSchema = exports.AssignmentExclusionIterable = exports.AssignmentFieldsSchema = exports.receivableRetroEvent = exports.ReceivableStatus = void 0;
const zod_1 = require("zod");
const CreditAgreement_1 = require("./CreditAgreement");
const CustomerProvideValues_1 = require("./CustomerProvideValues");
const Utils_1 = require("./Utils");
var ReceivableStatus;
(function (ReceivableStatus) {
    ReceivableStatus["Assigned"] = "assigned";
    ReceivableStatus["Unassigned"] = "unassigned";
})(ReceivableStatus || (exports.ReceivableStatus = ReceivableStatus = {}));
exports.receivableRetroEvent = [
    'retro-originate',
    'retro-assign',
    'retro-unassign',
];
const receivableNominalEvent = [
    'originate',
    'assign',
    'unassign',
    'transfer',
];
exports.AssignmentFieldsSchema = zod_1.z.object({
    assignedAt: zod_1.z.string(),
    creditFacilityId: zod_1.z.string(),
    reportingDate: Utils_1.ZodFullDateStrForJsonSchemaStruct,
    fileVersion: zod_1.z.number(),
    purchasePrice: zod_1.z.number().optional(),
    purchasePercentageRate: zod_1.z.number().optional(),
});
exports.AssignmentExclusionIterable = ['none', 'soft', 'hard'];
exports.AssignmentExclusionSchema = zod_1.z.enum(exports.AssignmentExclusionIterable);
// Zod schema for ReceivableBaseFields - record of string to Primitive
exports.ReceivableBaseFieldsSchema = zod_1.z.record(zod_1.z.string(), Utils_1.PrimitiveStruct);
var CreditFacility;
(function (CreditFacility) {
    // ============================================================================
    // ZOD SCHEMAS FOR RECEIVABLE RESPONSE TAPE
    // ============================================================================
    CreditFacility.RequiredIntermediateFieldsSchema = zod_1.z.object({
        assignmentExclusion: exports.AssignmentExclusionSchema,
        exposure: zod_1.z.number(),
        waiveAllEligibility: zod_1.z.boolean(),
        waiveAllConcentration: zod_1.z.boolean(),
        includeInConcentrationLimitDenominatorIfWaived: zod_1.z.boolean(),
        originalOwnerSlug: zod_1.z.string(),
        normalizedDurationDays: zod_1.z.number().nullable().optional(),
        normalizedisChargedOff: zod_1.z.boolean().nullable().optional(),
        normalizedisDelinquent: zod_1.z.boolean().nullable().optional(),
        normalizedDaysPastDue: zod_1.z.number().nullable().optional(),
        normalizedOriginationDate: zod_1.z.string().nullable().optional(),
        normalizedInitialBalance: zod_1.z.number().nullable().optional(),
    });
    CreditFacility.DynamicIntermediateFieldsSchema = exports.ReceivableBaseFieldsSchema;
    // required fields that will be auto-calculated from base/intermediate fields
    CreditFacility.PostProcessedFieldsSchema = zod_1.z.object({
        originalOwnerId: zod_1.z.string(),
        normalizedExposure: zod_1.z.number(),
        preassignmentCreditFacilityId: zod_1.z.string().nullable(),
        performingExposure: zod_1.z.number().optional(),
    });
    CreditFacility.ValuationBaseFieldsSchema = zod_1.z.object({
        valuation: zod_1.z.number(),
        valuationRate: zod_1.z.number(),
    });
    CreditFacility.ValuationBaseFieldsIterable = { valuation: 'valuation', valuationRate: 'valuationRate' };
    CreditFacility.EligibilityBaseFieldsSchema = zod_1.z.object({
        eligibleExposure: zod_1.z.number(),
        isEligible: zod_1.z.boolean(),
        eligibilityKickout: zod_1.z.number(),
    });
    CreditFacility.EligibilityBaseFieldsIterable = {
        eligibleExposure: 'eligibleExposure',
        isEligible: 'isEligible',
        eligibilityKickout: 'eligibilityKickout',
    };
    CreditFacility.EligibilityStatusIterable = ['ELIGIBLE', 'INELIGIBLE', 'INELIGIBLE_WAIVED'];
    CreditFacility.EligibilityStatusSchema = zod_1.z.enum(CreditFacility.EligibilityStatusIterable);
    CreditFacility.EligibilityStatusItemSchema = zod_1.z.object({
        eligibilityCriteriaKey: zod_1.z.string(),
        eligibilityStatus: CreditFacility.EligibilityStatusSchema,
    });
    CreditFacility.EligibilityStatusesSchema = zod_1.z.array(CreditFacility.EligibilityStatusItemSchema);
    CreditFacility.ConcentrationLimitBaseFieldsSchema = zod_1.z.object({
        eligibleExposureLessConcentrationLimitKickouts: zod_1.z.number(),
    });
    CreditFacility.ConcentrationLimitBaseFieldsIterable = {
        eligibleExposureLessConcentrationLimitKickouts: 'eligibleExposureLessConcentrationLimitKickouts',
    };
    CreditFacility.BorrowingBaseBaseFieldsSchema = zod_1.z.object({
        borrowingBaseAmount: zod_1.z.number(),
        advanceRate: zod_1.z.number(),
        advanceRatePath: zod_1.z.string().optional(),
    });
    CreditFacility.BorrowingBaseBaseFieldsIterable = {
        borrowingBaseAmount: 'borrowingBaseAmount',
        advanceRate: 'advanceRate',
        advanceRatePath: 'advanceRatePath',
    };
    CreditFacility.CreditFacilityReceivableSchema = zod_1.z.object({
        loanProductId: zod_1.z.string(),
        finleyReceivableId: zod_1.z.string(),
        externalReceivableId: zod_1.z.string(),
        baseFields: exports.ReceivableBaseFieldsSchema,
        dynamicIntermediateFields: CreditFacility.DynamicIntermediateFieldsSchema,
        requiredIntermediateFields: CreditFacility.RequiredIntermediateFieldsSchema,
        postProcessedFields: CreditFacility.PostProcessedFieldsSchema,
        assignmentFields: exports.AssignmentFieldsSchema.nullable(),
    });
    CreditFacility.ValuationReceivableSchema = CreditFacility.CreditFacilityReceivableSchema.extend({
        valuationFields: CreditFacility.ValuationBaseFieldsSchema,
    });
    CreditFacility.EligibilityReceivableSchema = CreditFacility.ValuationReceivableSchema.extend({
        eligibilityStatuses: CreditFacility.EligibilityStatusesSchema,
        eligibilityFields: CreditFacility.EligibilityBaseFieldsSchema,
    });
    CreditFacility.ConcentrationLimitReasonSchema = zod_1.z.object({
        propertyKey: zod_1.z.string(),
        concentrationLimitKickout: zod_1.z.number(),
    });
    CreditFacility.ConcentrationLimitWaiverSchema = zod_1.z.object({
        propertyKey: zod_1.z.string(),
        waiveReason: zod_1.z.string(),
        waivedAmount: zod_1.z.number().optional(),
    });
    CreditFacility.ConcentrationLimitReceivableSchema = CreditFacility.EligibilityReceivableSchema.extend({
        concentrationLimitReasons: zod_1.z.array(CreditFacility.ConcentrationLimitReasonSchema),
        concentrationLimitWaivers: zod_1.z.array(CreditFacility.ConcentrationLimitWaiverSchema),
        concentrationLimitFields: CreditFacility.ConcentrationLimitBaseFieldsSchema,
    });
    CreditFacility.BorrowingBaseReceivableSchema = CreditFacility.ConcentrationLimitReceivableSchema.extend({
        borrowingBaseFields: CreditFacility.BorrowingBaseBaseFieldsSchema,
    });
    CreditFacility.LoanTapeMetadataPrimitiveProperties = [
        'reportingDate',
        'fileVersion',
        'commitmentAmount',
        'drawRequestAmountForCalendarMonth',
        'principalBalance',
        'lastApprovedOrSubmittedFundingRequestDate',
    ];
    CreditFacility.StaticLoanTapeMetadataProperties = [
        ...CreditFacility.LoanTapeMetadataPrimitiveProperties,
        'configuredActiveWaivers',
        'previouslyAssigned',
    ];
    CreditFacility.DynamicLoanTapeMetadataProperties = [
        'portfolioCovenants',
        CustomerProvideValues_1.CUSTOMER_PROVIDED_VALUES,
    ];
    CreditFacility.LoanTapeMetadataIterable = [
        ...CreditFacility.StaticLoanTapeMetadataProperties,
        ...CreditFacility.DynamicLoanTapeMetadataProperties,
    ];
    CreditFacility.ReceivableResponseTapeSchema = zod_1.z.object({
        creditFacilityId: zod_1.z.string(),
        reportingDate: Utils_1.ZodFullDateStrForJsonSchemaStruct,
        fileVersion: zod_1.z.number(),
        receivables: zod_1.z.array(CreditFacility.BorrowingBaseReceivableSchema),
        concentrationLimitCalculations: zod_1.z.array(CreditAgreement_1.Config.ConcentrationLimit.Calculation.ConcentrationLimitCalculationSchema),
    });
    CreditFacility.CachedEligibilityReceivableSchema = zod_1.z.object({
        loanProductId: zod_1.z.string(),
        finleyReceivableId: zod_1.z.string(),
        eligibilityStatuses: CreditFacility.EligibilityStatusesSchema,
        eligibilityFields: CreditFacility.EligibilityBaseFieldsSchema,
    });
    CreditFacility.ReceivableResponseTapePreviewEligibilityStatusesSchema = zod_1.z.object({
        receivables: zod_1.z.array(CreditFacility.CachedEligibilityReceivableSchema),
    });
})(CreditFacility || (exports.CreditFacility = CreditFacility = {}));
var Preview;
(function (Preview) {
    let ReceivableResponseTapePreviewStatus;
    (function (ReceivableResponseTapePreviewStatus) {
        ReceivableResponseTapePreviewStatus["Completed"] = "completed";
        ReceivableResponseTapePreviewStatus["Failed"] = "failed";
        ReceivableResponseTapePreviewStatus["Initiated"] = "initiated";
        ReceivableResponseTapePreviewStatus["Started"] = "started";
    })(ReceivableResponseTapePreviewStatus = Preview.ReceivableResponseTapePreviewStatus || (Preview.ReceivableResponseTapePreviewStatus = {}));
    Preview.SignaledAssignmentsSchema = zod_1.z.record(zod_1.z.string(), zod_1.z.array(zod_1.z.string()));
    Preview.ReceivableResponseTapePreviewPayloadSchema = zod_1.z.object({
        masterLoanTapeIds: zod_1.z.array(zod_1.z.string()),
        signaledAssignments: Preview.SignaledAssignmentsSchema,
    });
    Preview.AssignmentBorrowingBaseReportSchema = zod_1.z.object({
        creditFacilityId: zod_1.z.string(),
        totalExposure: zod_1.z.number(),
        totalExposureByLoanProductId: zod_1.z.record(zod_1.z.string(), zod_1.z.number()),
        totalEligibilityKickoutByLoanProductId: zod_1.z.record(zod_1.z.string(), zod_1.z.number()),
        totalExposureLessEligibilityKickoutsByLoanProductId: zod_1.z.record(zod_1.z.string(), zod_1.z.number()),
        totalBorrowingBaseAmount: zod_1.z.number(),
        totalBorrowingBaseAmountByLoanProductId: zod_1.z.record(zod_1.z.string(), zod_1.z.number()),
        totalProposedAssignedReceivableCount: zod_1.z.number(),
        proposedAssignedReceivableCountByLoanProductId: zod_1.z.record(zod_1.z.string(), zod_1.z.number()),
        totalEligibleExposureLessConcentrationKickouts: zod_1.z.number(),
        effectiveReceivableAdvanceRate: zod_1.z.number(),
        effectiveReceivableAdvanceRateByLoanProductId: zod_1.z.record(zod_1.z.string(), zod_1.z.number()),
        balanceSheetExposure: zod_1.z.number(),
        concentrationLimitCalculations: zod_1.z.array(CreditAgreement_1.Config.ConcentrationLimit.Calculation.ConcentrationLimitCalculationSchema),
        eligibilityCriteriaCalculations: zod_1.z.array(CreditAgreement_1.Config.EligibilityTest.EligibilityCriteriaCalculationSchema),
    });
    Preview.AssignmentReceivablesPreviewAttachmentBaseSchema = zod_1.z.object({
        creditFacilityId: zod_1.z.string(),
        receivableResponseTapePreviewId: zod_1.z.string(),
        reportingDate: Utils_1.ZodFullDateStrForJsonSchemaStruct,
        fileVersion: zod_1.z.number(),
    });
    Preview.AssignmentBorrowingBaseReportForPreviewSchema = Preview.AssignmentBorrowingBaseReportSchema.extend({
        totalAssignableExposureByLoanProductId: zod_1.z.record(zod_1.z.string(), zod_1.z.number()),
        balanceSheetExposureByLoanProductId: zod_1.z.record(zod_1.z.string(), zod_1.z.number()),
        balanceSheetReceivableCount: zod_1.z.number(),
        balanceSheetReceivableCountByLoanProductId: zod_1.z.record(zod_1.z.string(), zod_1.z.number()),
        eligibleExposureOnBalanceSheet: zod_1.z.number(),
        eligibleExposureOnBalanceSheetByLoanProductId: zod_1.z.record(zod_1.z.string(), zod_1.z.number()),
        newlyProposedExposure: zod_1.z.number(),
    });
    Preview.AssignmentSummaryPreviewAttachmentSchema = Preview.AssignmentReceivablesPreviewAttachmentBaseSchema.extend({
        type: zod_1.z.literal('assignmentSummaryPreview'),
        borrowingBaseReport: Preview.AssignmentBorrowingBaseReportForPreviewSchema,
        countsByStatus: zod_1.z.object({
            proposedAssignedReceivables: zod_1.z.record(zod_1.z.string(), // loanProductId
            zod_1.z.record(zod_1.z.string(), zod_1.z.number())),
            leftOnBalanceSheetReceivables: zod_1.z.record(zod_1.z.string(), zod_1.z.number()),
        }),
        previewIdentifier: zod_1.z.string(),
        isInitialCondition: zod_1.z.union([zod_1.z.boolean(), zod_1.z.null(), zod_1.z.undefined()]),
    });
})(Preview || (exports.Preview = Preview = {}));
var ReceivableResponseTapePreviewJobs;
(function (ReceivableResponseTapePreviewJobs) {
    ReceivableResponseTapePreviewJobs.terminalStatuses = [
        'completed',
        'error',
    ];
    ReceivableResponseTapePreviewJobs.statuses = [
        'inProgress',
        ...ReceivableResponseTapePreviewJobs.terminalStatuses,
    ];
    ReceivableResponseTapePreviewJobs.assignmentMethod = ['csv', 'strategy', 'assignNone'];
    ReceivableResponseTapePreviewJobs.defaultPreviewEmailStatus = [
        'none',
        'generating',
        'notIncludedInEmail',
        'includedInEmail',
    ];
    ReceivableResponseTapePreviewJobs.AssignmentMethodSchema = zod_1.z.enum(ReceivableResponseTapePreviewJobs.assignmentMethod);
    ReceivableResponseTapePreviewJobs.AggregatedAssignmentSummaryPreviewSchema = zod_1.z.object({
        receivableResponseTapePreviewJobId: zod_1.z.string(),
        reportingDate: Utils_1.ZodFullDateStrForJsonSchemaStruct,
        fileVersion: zod_1.z.number(),
        previewIdentifier: zod_1.z.string(),
        implementationId: zod_1.z.string(),
        assignmentMethod: ReceivableResponseTapePreviewJobs.AssignmentMethodSchema,
        strategyId: zod_1.z.string().nullable(),
        borrowingBaseReportMap: zod_1.z.record(zod_1.z.string(), // creditFacilityId
        Preview.AssignmentBorrowingBaseReportForPreviewSchema),
    });
})(ReceivableResponseTapePreviewJobs || (exports.ReceivableResponseTapePreviewJobs = ReceivableResponseTapePreviewJobs = {}));
var Assignment;
(function (Assignment) {
    let AssignmentConfirmationJob;
    (function (AssignmentConfirmationJob) {
        AssignmentConfirmationJob.AssignmentConfirmationJobTypeIterable = [
            'assignment',
            'repurchase',
            'transfer',
        ];
        AssignmentConfirmationJob.AssignmentConfirmationJobTypeSchema = zod_1.z.enum(AssignmentConfirmationJob.AssignmentConfirmationJobTypeIterable);
        AssignmentConfirmationJob.ReassignmentConfirmationJobReceivablesPayloadItemSchema = zod_1.z.object({
            loanProductId: zod_1.z.string(),
            externalReceivableIds: zod_1.z.array(zod_1.z.string()),
            creditFacilityId: zod_1.z.string().nullable(),
        });
        AssignmentConfirmationJob.ReassignmentConfirmationJobReceivablesPayloadSchema = zod_1.z.array(AssignmentConfirmationJob.ReassignmentConfirmationJobReceivablesPayloadItemSchema);
    })(AssignmentConfirmationJob = Assignment.AssignmentConfirmationJob || (Assignment.AssignmentConfirmationJob = {}));
})(Assignment || (exports.Assignment = Assignment = {}));
exports.SingleReceivableResponseTapePreviewPayloadSchema = zod_1.z.object({
    creditFacilityId: zod_1.z.string(),
    masterLoanTapeIds: zod_1.z.array(zod_1.z.string()),
    previewIdentifier: zod_1.z.string(),
    receivableResponseTapePreviewJobId: zod_1.z.string(),
});
exports.ReceivableResponseTapePreviewsFromStrategyPayloadSchema = zod_1.z.object({
    assignmentStrategyId: zod_1.z.string(),
    masterLoanTapeIds: zod_1.z.array(zod_1.z.string()),
    receivableResponseTapePreviewJobId: zod_1.z.string(),
});
exports.AssignmentConfirmationJobPayloadSchema = zod_1.z.object({
    assignmentConfirmationJobId: zod_1.z.string(),
    jobType: Assignment.AssignmentConfirmationJob.AssignmentConfirmationJobTypeSchema,
});
exports.DefaultPreviewEmailPayloadSchema = zod_1.z.object({
    loanProductIds: zod_1.z.array(zod_1.z.string()),
    implementationId: zod_1.z.string(),
    fileVersion: zod_1.z.number(),
    reportingDate: Utils_1.ZodFullDateStrForJsonSchemaStruct,
});
//# sourceMappingURL=Receivable.js.map